/**
 * English display names for the German room, floor and device names of the Gira HomeServer (KNX tab).
 *
 * Display only: grouping, sorting and track-by keys keep using the original names. German (any "de…"
 * language) shows the HomeServer names unchanged; every other language gets English (the app's
 * fallback language).
 *
 * Lookup order (non-German):
 *   1. exact name per kind (room / floor / device: "Heizung" is a Boiler room as a room, Heating as a device)
 *   2. "<base> <number>" → translated base + the number ("Büro 2" → "Office 2", "Steckdose 3" → "Socket 3")
 *   3. structural rules: device head + qualifiers ("Besch. Süd" → "South blind", "Deckenlampe Küche" →
 *      "Ceiling light (kitchen)", "Steckdose links" → "Socket left"), two-word rooms around a room noun
 *      ("Gäste Bad" → "Guest bathroom")
 *   4. word by word (incl. simple German compounds such as "Küchenlicht" → "Kitchen light");
 *      unknown words stay as they are
 */

export type KnxNameKind = 'room' | 'floor' | 'device';

// ── Exact names (the HomeServer's current names, plus obvious siblings) ─────────────────────────────

const FLOORS: Readonly<Record<string, string>> = {
  'Außen':          'Outside',
  'Aussen':         'Outside',
  'Erdgeschoss':    'Ground floor',
  'Obergeschoss':   'Upper floor',
  'Dachgeschoss':   'Attic floor',
  'Untergeschoss':  'Basement',
  'Kellergeschoss': 'Basement',
  'Keller':         'Basement',
};

const ROOMS: Readonly<Record<string, string>> = {
  'Abstellraum':  'Storage room',
  'Archiv':       'Archive',
  'Archiv Flur':  'Archive hallway',
  'Bad':          'Bathroom',
  'Bad Eltern':   "Parents' bathroom",
  'Bad Kind':     "Kids' bathroom",
  'Bibliothek':   'Library',
  'Büro':         'Office',
  'Eingang':      'Entrance',
  'Esszimmer':    'Dining room',
  'Flur':         'Hallway',
  'Flur Hobby':   'Hobby room hallway',
  'Garten':       'Garden',
  'Getränke':     'Beverage room',
  'Gäste':        'Guest room',
  'Heizung':      'Boiler room',
  'Hobby':        'Hobby room',
  'Kind':         "Kid's room",
  'Küche':        'Kitchen',
  'Obergeschoss': 'Upper floor',
  'Schlafzimmer': 'Bedroom',
  'Technik':      'Utility room',
  'Technikraum':  'Utility room',
  'WC':           'WC',
  'WC Vorraum':   'WC anteroom',
  'Wohnzimmer':   'Living room',
};

const DEVICES: Readonly<Record<string, string>> = {
  'Balkon':           'Balcony',
  'Besch. Balkon':    'Balcony blind',
  'Besch. Garten':    'Garden blind',
  'Besch. Nord':      'North blind',
  'Besch. Straße':    'Street blind',
  'Besch. Terrasse':  'Terrace blind',
  'Besch. links':     'Blind left',
  'Besch. rechts':    'Blind right',
  'Beschattung':      'Blind',
  'Deckenlampe':      'Ceiling light',
  'Deckenlampe Bett': 'Ceiling light (bed)',
  'Deckenlampe Wand': 'Ceiling light (wall)',
  'Heizung':          'Heating',
  'Indirekt':         'Indirect light',
  'Nordwand':         'North wall',
  'Spiegellampe':     'Mirror light',
  'Steckdose':        'Socket',
  'Steckdose links':  'Socket left',
  'Steckdose rechts': 'Socket right',
  'Straße':           'Street',
  'Terrasse':         'Terrace',
};

// ── Word-level fallback vocabulary (keys lower case) ───────────────────────────────────────────────

const WORDS: Readonly<Record<string, string>> = {
  // lights, sockets, blinds and other fittings
  'deckenlampe': 'Ceiling light', 'deckenleuchte': 'Ceiling light', 'deckenspots': 'Ceiling spots',
  'lampe': 'Light', 'leuchte': 'Light', 'licht': 'Light', 'beleuchtung': 'Lighting', 'lichtband': 'Light strip',
  'spot': 'Spot', 'spots': 'Spots', 'strahler': 'Spotlight', 'led': 'LED', 'ledstreifen': 'LED strip', 'streifen': 'Strip',
  'wandlampe': 'Wall light', 'wandleuchte': 'Wall light', 'stehlampe': 'Floor lamp', 'tischlampe': 'Table lamp',
  'leselampe': 'Reading lamp', 'pendelleuchte': 'Pendant light', 'spiegellampe': 'Mirror light',
  'indirekt': 'Indirect light', 'steckdose': 'Socket', 'steckdosen': 'Sockets',
  'beschattung': 'Blind', 'rollladen': 'Shutter', 'rolladen': 'Shutter', 'rollo': 'Roller blind',
  'jalousie': 'Blind', 'raffstore': 'Blind', 'markise': 'Awning',
  'fenster': 'Window', 'tür': 'Door', 'tor': 'Gate', 'heizung': 'Heating', 'lüftung': 'Ventilation',
  'pumpe': 'Pump', 'brunnen': 'Fountain', 'bewässerung': 'Irrigation', 'spiegel': 'Mirror',
  // positions and directions
  'links': 'left', 'rechts': 'right', 'oben': 'top', 'unten': 'bottom', 'mitte': 'middle',
  'vorne': 'front', 'vorn': 'front', 'hinten': 'back', 'innen': 'inside', 'außen': 'Outside', 'aussen': 'Outside',
  'nord': 'North', 'süd': 'South', 'sued': 'South', 'ost': 'East', 'west': 'West',
  'nordost': 'North-east', 'nordwest': 'North-west', 'südost': 'South-east', 'südwest': 'South-west',
  // rooms and places
  'abstellraum': 'Storage room', 'ankleide': 'Dressing room', 'arbeitszimmer': 'Study', 'archiv': 'Archive',
  'bad': 'Bathroom', 'badezimmer': 'Bathroom', 'balkon': 'Balcony', 'bett': 'Bed', 'bibliothek': 'Library',
  'boden': 'Floor', 'büro': 'Office', 'carport': 'Carport', 'couch': 'Couch', 'dach': 'Roof',
  'dachboden': 'Attic', 'decke': 'Ceiling', 'diele': 'Hallway', 'einfahrt': 'Driveway', 'eingang': 'Entrance',
  'eingangsbereich': 'Entrance area', 'eltern': 'Parents', 'essbereich': 'Dining area', 'essen': 'Dining',
  'esstisch': 'Dining table', 'esszimmer': 'Dining room', 'fassade': 'Facade', 'flur': 'Hallway',
  'galerie': 'Gallery', 'garage': 'Garage', 'garten': 'Garden', 'gäste': 'Guest', 'gästezimmer': 'Guest room',
  'getränke': 'Beverages', 'haus': 'House', 'haustür': 'Front door', 'hauswirtschaftsraum': 'Utility room',
  'hobby': 'Hobby', 'hof': 'Courtyard', 'hwr': 'Utility room', 'kammer': 'Closet', 'keller': 'Basement',
  'kind': 'Kid', 'kinder': 'Kids', 'kinderzimmer': "Kid's room", 'küche': 'Kitchen', 'lager': 'Storage',
  'pool': 'Pool', 'raum': 'Room', 'sauna': 'Sauna', 'schlafzimmer': 'Bedroom', 'sofa': 'Sofa',
  'speisekammer': 'Pantry', 'spielzimmer': 'Playroom', 'straße': 'Street', 'strasse': 'Street',
  'technik': 'Utility', 'technikraum': 'Utility room', 'terrasse': 'Terrace', 'tisch': 'Table',
  'treppe': 'Stairs', 'treppenhaus': 'Staircase', 'vorgarten': 'Front garden', 'vorraum': 'Anteroom',
  'wand': 'Wall', 'waschküche': 'Laundry room', 'wc': 'WC', 'werkstatt': 'Workshop', 'windfang': 'Porch',
  'wintergarten': 'Conservatory', 'wohnen': 'Living', 'wohnzimmer': 'Living room', 'zimmer': 'Room',
  // floors
  'erdgeschoss': 'Ground floor', 'obergeschoss': 'Upper floor', 'dachgeschoss': 'Attic floor',
  'untergeschoss': 'Basement', 'kellergeschoss': 'Basement',
  // misc
  'und': 'and', 'alle': 'All', 'gesamt': 'All', 'zentral': 'Central',
};

/** Abbreviations the HomeServer names use, expanded before the word lookup. */
const ABBREVIATIONS: Readonly<Record<string, string>> = { 'besch.': 'Beschattung', 'str.': 'Straße' };

/** German compound endings ("Küchen|licht", "Nord|wand"): prefix word + this English noun. Longest first. */
const COMPOUND_SUFFIXES: readonly (readonly [string, string])[] = ([
  ['steckdosen', 'sockets'], ['steckdose', 'socket'], ['beleuchtung', 'lighting'], ['beschattung', 'blind'],
  ['rollladen', 'shutter'], ['rolladen', 'shutter'], ['jalousie', 'blind'], ['markise', 'awning'],
  ['leuchte', 'light'], ['lampe', 'light'], ['licht', 'light'], ['strahler', 'spotlight'], ['spots', 'spots'],
  ['zimmer', 'room'], ['raum', 'room'], ['bad', 'bathroom'], ['flur', 'hallway'], ['küche', 'kitchen'],
  ['keller', 'basement'], ['bereich', 'area'], ['garten', 'garden'], ['terrasse', 'terrace'], ['balkon', 'balcony'],
  ['wand', 'wall'], ['decke', 'ceiling'], ['fenster', 'window'], ['tür', 'door'], ['tor', 'gate'], ['seite', 'side'],
] as [string, string][]).sort((a, b) => b[0].length - a[0].length);

/** Linking letters between the parts of a compound ("Küche-n-licht", "Arbeit-s-zimmer"). */
const LINKING = ['en', 'n', 's', 'es', 'er'];

/** First word of a device name that the rest qualifies; `blind` heads put the qualifier in front ("South blind"). */
const DEVICE_HEADS: Readonly<Record<string, { en: string; blind: boolean }>> = {
  'deckenlampe':   { en: 'Ceiling light', blind: false },
  'deckenleuchte': { en: 'Ceiling light', blind: false },
  'lampe':         { en: 'Light',         blind: false },
  'leuchte':       { en: 'Light',         blind: false },
  'licht':         { en: 'Light',         blind: false },
  'spot':          { en: 'Spot',          blind: false },
  'spots':         { en: 'Spots',         blind: false },
  'strahler':      { en: 'Spotlight',     blind: false },
  'wandlampe':     { en: 'Wall light',    blind: false },
  'wandleuchte':   { en: 'Wall light',    blind: false },
  'stehlampe':     { en: 'Floor lamp',    blind: false },
  'pendelleuchte': { en: 'Pendant light', blind: false },
  'spiegellampe':  { en: 'Mirror light',  blind: false },
  'indirekt':      { en: 'Indirect light', blind: false },
  'led':           { en: 'LED',           blind: false },
  'steckdose':     { en: 'Socket',        blind: false },
  'steckdosen':    { en: 'Sockets',       blind: false },
  'beschattung':   { en: 'Blind',         blind: true },
  'jalousie':      { en: 'Blind',         blind: true },
  'raffstore':     { en: 'Blind',         blind: true },
  'rollladen':     { en: 'Shutter',       blind: true },
  'rolladen':      { en: 'Shutter',       blind: true },
  'rollo':         { en: 'Roller blind',  blind: true },
  'markise':       { en: 'Awning',        blind: true },
};

/** Position words that follow the device noun ("Socket left", "Blind right"). */
const POSITIONS: Readonly<Record<string, string>> = {
  'links': 'left', 'rechts': 'right', 'oben': 'top', 'unten': 'bottom', 'mitte': 'middle',
  'vorne': 'front', 'vorn': 'front', 'hinten': 'back', 'innen': 'inside',
};

/** Room nouns of two-word room names: the other word goes in front ("Gäste Bad" → "Guest bathroom"). */
const ROOM_HEADS: Readonly<Record<string, string>> = {
  'bad': 'bathroom', 'flur': 'hallway', 'zimmer': 'room', 'raum': 'room', 'büro': 'office',
  'wc': 'WC', 'vorraum': 'anteroom',
};

/** Floor codes stay as they are: "E0"…"E5", "EG", "OG", "1.OG", "UG", "DG", "KG". */
const FLOOR_CODE = /^(?:E-?\d+|\d*\.?\s*(?:EG|OG|UG|DG|KG)\d*)$/i;

// ── Lookup helpers ─────────────────────────────────────────────────────────────────────────────────

/** Maps, not plain objects: a name such as "constructor" must not hit Object.prototype. */
const toMap = <V>(o: Readonly<Record<string, V>>, lower = false): ReadonlyMap<string, V> =>
  new Map(Object.entries(o).map(([k, v]) => [lower ? k.toLowerCase() : k, v]));

const EXACT: Readonly<Record<KnxNameKind, ReadonlyMap<string, string>>> = {
  room: toMap(ROOMS), floor: toMap(FLOORS), device: toMap(DEVICES),
};
const EXACT_CI: Readonly<Record<KnxNameKind, ReadonlyMap<string, string>>> = {
  room: toMap(ROOMS, true), floor: toMap(FLOORS, true), device: toMap(DEVICES, true),
};
const WORD_MAP   = toMap(WORDS);
const ABBREV_MAP = toMap(ABBREVIATIONS);
const HEAD_MAP   = toMap(DEVICE_HEADS);
const POS_MAP    = toMap(POSITIONS);
const ROOM_HEAD_MAP = toMap(ROOM_HEADS);

function exact(text: string, kind: KnxNameKind): string | undefined {
  return EXACT[kind].get(text) ?? EXACT_CI[kind].get(text.toLowerCase());
}

const capFirst = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
/** Lower case for use inside a phrase, but keep acronyms ("LED", "WC"). */
const inPhrase = (s: string): string => (s.length > 1 && s === s.toUpperCase() ? s : s.toLowerCase());

/** A compound such as "Küchenlicht" → "Kitchen light"; null when the parts are not both known. */
function compound(lc: string): string | null {
  for (const [suffix, en] of COMPOUND_SUFFIXES) {
    if (lc.length <= suffix.length + 2 || !lc.endsWith(suffix)) continue;
    const prefix = lc.slice(0, -suffix.length).replace(/-$/, '');
    const candidates = [prefix, ...LINKING.filter(l => prefix.endsWith(l)).map(l => prefix.slice(0, -l.length))];
    for (const p of candidates) {
      const pre = p.length >= 3 ? WORD_MAP.get(p) : undefined;
      if (pre) return `${capFirst(pre)} ${en}`;
    }
  }
  return null;
}

/** One word (surrounding punctuation kept); null when it is unknown. */
function word(token: string): string | null {
  const m = /^([^\p{L}\p{N}]*)(.*?)([^\p{L}\p{N}]*)$/u.exec(token);
  if (!m || !m[2]) return null;
  const [, lead, core, trail] = m;
  const lc = core.toLowerCase();
  let en: string | null = WORD_MAP.get(lc) ?? null;
  if (en == null && core.includes('-')) {
    // "Gäste-WC": translate the parts; keep the hyphen only when a part stayed German.
    const raw = core.split('-');
    const parts = raw.map(p => (p ? word(p) : null));
    if (parts.some(p => p != null)) {
      en = parts.every(p => p != null) ? parts.join(' ') : raw.map((p, i) => parts[i] ?? p).join('-');
    }
  }
  en ??= compound(lc);
  return en == null ? null : lead + en + trail;
}

const wordOrSelf = (token: string): string => word(token) ?? token;

/** "Besch. Süd" → "South blind", "Steckdose links" → "Socket left", "Deckenlampe Küche" → "Ceiling light (kitchen)". */
function deviceRule(tokens: string[]): string | null {
  const head = tokens.length > 1 ? HEAD_MAP.get(tokens[0].toLowerCase()) : undefined;
  if (!head) return null;
  const pos: string[] = [];
  const quals: { text: string; known: boolean }[] = [];
  for (const t of tokens.slice(1)) {
    const p = POS_MAP.get(t.toLowerCase());
    if (p) { pos.push(p); continue; }
    const en = word(t);
    quals.push(en != null ? { text: en, known: true } : { text: t, known: false });
  }
  const posPart = pos.length ? ` ${pos.join(' ')}` : '';
  if (!quals.length) return head.en + posPart;
  if (head.blind) return `${capFirst(quals.map(q => q.text).join(' '))} ${inPhrase(head.en)}${posPart}`;
  // Unknown (German) words keep their capital; translated ones read as a lower-case qualifier.
  return `${head.en}${posPart} (${quals.map(q => (q.known ? inPhrase(q.text) : q.text)).join(' ')})`;
}

/** "Gäste Bad" / "Bad Gäste" → "Guest bathroom", "Keller Flur" → "Basement hallway". */
function roomRule(tokens: string[]): string | null {
  if (tokens.length !== 2) return null;
  const hi = ROOM_HEAD_MAP.has(tokens[1].toLowerCase()) ? 1 : ROOM_HEAD_MAP.has(tokens[0].toLowerCase()) ? 0 : -1;
  if (hi < 0) return null;
  return `${capFirst(wordOrSelf(tokens[1 - hi]))} ${ROOM_HEAD_MAP.get(tokens[hi].toLowerCase())}`;
}

function translate(text: string, kind: KnxNameKind): string {
  const hit = exact(text, kind);
  if (hit != null) return hit;
  if (kind === 'floor' && FLOOR_CODE.test(text)) return text;

  // "<base> <number>": "Büro 2", "Deckenlampe 3", "Flur 1a"
  const num = /^(.*\S)\s+(\d+[a-z]?)$/i.exec(text);
  if (num) return `${translate(num[1], kind)} ${num[2]}`;

  const tokens = text.split(/\s+/).map(t => ABBREV_MAP.get(t.toLowerCase()) ?? t);
  const expanded = tokens.join(' ');
  if (expanded !== text) {
    const hit2 = exact(expanded, kind);
    if (hit2 != null) return hit2;
  }
  const structural = kind === 'device' ? deviceRule(tokens) : kind === 'room' ? roomRule(tokens) : null;
  return structural ?? tokens.map(wordOrSelf).join(' ');
}

// ── Public API ─────────────────────────────────────────────────────────────────────────────────────

/** True for German UI languages ("de", "de-AT", …): those show the HomeServer names unchanged. */
export function isGermanLang(lang: string | null | undefined): boolean {
  return !!lang && lang.toLowerCase().startsWith('de');
}

const CACHE = new Map<string, string>();
const CACHE_MAX = 2000;

/** Display name of a KNX room, floor or device for the UI language (null / undefined → ""). */
export function knxName(text: string | null | undefined, lang: string | null | undefined, kind: KnxNameKind): string {
  if (text == null) return '';
  if (isGermanLang(lang)) return text;
  const trimmed = text.trim();
  if (!trimmed) return text;
  const key = `${kind}\u0001${trimmed}`;
  let out = CACHE.get(key);
  if (out === undefined) {
    out = translate(trimmed, kind);
    if (CACHE.size >= CACHE_MAX) CACHE.clear();
    CACHE.set(key, out);
  }
  return out;
}

/** "E3 Living room" / "Outside" / "": floor and room translated separately. */
export function knxLocationName(floor: string | null | undefined, room: string | null | undefined, lang: string | null | undefined): string {
  return [knxName(floor, lang, 'floor'), knxName(room, lang, 'room')].filter(Boolean).join(' ');
}

/** A "<floor> <room>" composite such as "E3 Wohnzimmer" (leading floor code split off); otherwise a room name. */
export function knxCompositeName(name: string | null | undefined, lang: string | null | undefined): string {
  if (name == null) return '';
  if (isGermanLang(lang)) return name;
  const m = /^(\S+)\s+(.+)$/.exec(name.trim());
  if (m && (FLOOR_CODE.test(m[1]) || exact(m[1], 'floor') != null)) return knxLocationName(m[1], m[2], lang);
  return knxName(name, lang, 'room');
}

/** Label of a KNX temperature reading: its floor / room fields when both are set, else its "<floor> <room>" name. */
export function knxReadingName(t: { name: string; floor?: string | null; room?: string | null }, lang: string | null | undefined): string {
  return t.floor && t.room ? knxLocationName(t.floor, t.room, lang) : knxCompositeName(t.name, lang);
}
